import { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  FileSpreadsheet, Upload, Loader2, Trash2, CheckCircle2, XCircle,
} from 'lucide-react';

const fmtC = (v) => v == null ? '\u2014' : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (d) => d ? new Date(d).toLocaleDateString() : '\u2014';

export default function UploadCreditFilePage({ noLayout = false }) {
  const [partners, setPartners] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [partner, setPartner] = useState('Anchor');
  const [accountId, setAccountId] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPartner, setFilterPartner] = useState('all');

  const fetchUploads = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterPartner !== 'all' ? { partner: filterPartner } : {};
      const { data } = await api.get('/bank-statements/uploads', { params });
      setUploads(data.uploads || []);
    } catch (e) {
      toast.error('Failed to load uploads');
    } finally {
      setLoading(false);
    }
  }, [filterPartner]);

  useEffect(() => {
    Promise.all([
      api.get('/bank-statements/partners'),
      api.get('/super-admin/accounts'),
    ]).then(([p, o]) => {
      setPartners(p.data.partners || []);
      const accts = (o.data || []).filter(a => a.status === 'active' && a.organisation_name);
      setOrgs(accts.map(a => ({ account_id: a.id, org_name: a.organisation_name })));
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchUploads(); }, [fetchUploads]);

  const onFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xls|xlsx)$/i.test(f.name)) {
      toast.error('Only .xls or .xlsx files allowed');
      return;
    }
    setFile(f);
  };

  const onUpload = async () => {
    if (!file || !partner || !accountId) {
      toast.error('Pick partner, customer and file first');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('partner', partner);
    fd.append('account_id', accountId);
    try {
      const { data } = await api.post('/bank-statements/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${data.rows} rows · Credits ${fmtC(data.total_credits)} · Debits ${fmtC(data.total_debits)}`);
      setFile(null);
      fetchUploads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (upload_id) => {
    if (!window.confirm('Soft-delete this upload? Lines will be excluded from reports.')) return;
    try {
      await api.delete(`/bank-statements/uploads/${upload_id}`);
      toast.success('Upload removed');
      fetchUploads();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // Conditionally wrap in MainLayout — when embedded inside SuperAdmin, skip the wrapper.
  const Wrapper = noLayout ? ({ children }) => <>{children}</> : MainLayout;

  return (
    <Wrapper>
      <div className="space-y-5" data-testid="upload-credit-file-page">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Upload Credit File</h2>
          <p className="text-sm text-muted-foreground">
            Upload bank statement <code className="text-xs bg-muted px-1 rounded">.xls / .xlsx</code> per Partner + Customer.
            Credit lines = Funds Pulled. Debit lines = Vendor payments.
          </p>
        </div>

        {/* Upload Form */}
        <div className="border rounded-lg p-5 bg-card" data-testid="upload-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Partner *</label>
              <Select value={partner} onValueChange={setPartner}>
                <SelectTrigger className="h-9 text-sm" data-testid="upload-partner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {partners.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Customer (Org) *</label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger className="h-9 text-sm" data-testid="upload-org-select"><SelectValue placeholder="Select org..." /></SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  {orgs.sort((a, b) => a.org_name.localeCompare(b.org_name)).map(o => (
                    <SelectItem key={o.account_id} value={o.account_id}>
                      {o.org_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Statement File *</label>
              <Input
                type="file"
                accept=".xls,.xlsx"
                onChange={onFileSelect}
                className="h-9 text-sm"
                data-testid="upload-file-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={onUpload}
              disabled={!file || !partner || !accountId || uploading}
              data-testid="upload-submit-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? 'Uploading...' : 'Upload Credit File'}
            </Button>
            {file && (
              <span className="text-xs text-muted-foreground">
                <FileSpreadsheet className="h-3 w-3 inline mr-1" />{file.name} ({(file.size / 1024).toFixed(1)} KB)
              </span>
            )}
          </div>
        </div>

        {/* Uploads History */}
        <div className="border rounded-lg bg-card">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold">Past Uploads</h3>
            <Select value={filterPartner} onValueChange={setFilterPartner}>
              <SelectTrigger className="h-8 text-xs w-[160px]" data-testid="filter-partner-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partners</SelectItem>
                {partners.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="uploads-table">
              <thead className="bg-muted/30 border-b">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Partner</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Customer</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">File</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Period</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Rows</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Credits</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Debits</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Open Bal</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Close Bal</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Uploaded</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}><td colSpan={11} className="px-3 py-2"><Skeleton className="h-4 w-full" /></td></tr>
                  ))
                ) : uploads.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-12 text-muted-foreground">
                      <FileSpreadsheet className="h-10 w-10 mx-auto mb-2 opacity-30" />
                      <p>No uploads yet</p>
                    </td>
                  </tr>
                ) : (
                  uploads.map((u, i) => (
                    <tr key={u.upload_id} className="border-b border-border/40 hover:bg-muted/30" data-testid={`upload-row-${i}`}>
                      <td className="px-3 py-2 font-semibold">{u.partner}</td>
                      <td className="px-3 py-2">{u.org_name}</td>
                      <td className="px-3 py-2 font-mono text-[11px] truncate max-w-[180px]" title={u.file_name}>{u.file_name}</td>
                      <td className="px-3 py-2 text-muted-foreground text-[11px]">{fmtD(u.statement_period_start)} – {fmtD(u.statement_period_end)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{u.row_count.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtC(u.total_credits)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-700">{fmtC(u.total_debits)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmtC(u.opening_balance)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtC(u.closing_balance)}</td>
                      <td className="px-3 py-2 text-muted-foreground text-[11px]">{fmtD(u.uploaded_at)}<br/><span className="text-[10px]">{u.uploaded_by_name}</span></td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => onDelete(u.upload_id)}
                          className="p-1 hover:text-red-600 text-muted-foreground transition-colors"
                          data-testid={`delete-upload-${i}`}
                          title="Soft-delete this upload"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}
